import React from 'react';

// Assets
import { ReactComponent as ConquestBadge } from '@/images/design-system-v2/proposal-icons/conquest-badge.svg?react';
import { ReactComponent as SuccessIcon } from '@/images/design-system-v2/proposal-icons/icon-default-success.svg?react';
import { ReactComponent as WarningIcon } from '@/images/design-system-v2/proposal-icons/icon-default-warning.svg?react';

// Components
import { CurrencyCell, NoDataCell, PercentageCell, TextCell } from '@/views/components/cells';
import { LoadingSpinner } from '@/views/components/common';
import { ParenthesesLabel, PillLabel } from '@/views/components/misc';

// Libraries
import classNames from 'classnames';

// Types
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import PlanningSummaryValue from '@/types/planning-summary-value';

// Utils
import { formatLocalizedString, formatMessageWithLocale, FormattedHTMLMessage, LOCALE } from '@/utils/i18n';
import { PILL_COLORS } from '@/utils/colors';

type Props = {
  value?: PlanningSummaryValue;
  className?: string;
  showBaseCurrencies?: boolean;
  locale?: Locale;
  decimalPlace?: number;
};

const PlanningSummaryCell: React.FunctionComponent<Props> = ({ value, className, locale, showBaseCurrencies, decimalPlace }: Props) => {
  if (value == null || !value.planAccountId) {
    return <NoDataCell />;
  }

  const { planAccount, portfolioName, currency, onTrackAlignment } = value;
  const isOnTrack = !!(onTrackAlignment?.assetAllocation && onTrackAlignment?.rateOfReturn);
  const i18nBase = 'Proposal.Planning.Validation';
  const i18nValidationKey = `${i18nBase}.${isOnTrack ? 'OnTrack' : 'NeedsReview'}`;

  // TODO: if necessary, this must be dynamic adding a plan provider from the data
  // for now, we are using Conquest as the only provider
  const localizedProvider: LocalizedString = { [LOCALE.EN_CA]: 'Conquest', [LOCALE.FR_CA]: 'Conquest' };
  const providerName: string = localizedProvider[locale || LOCALE.EN_CA] || '';

  const renderSummaryValidation = () => (
    <div className="planning-summary-validation">
      <ConquestBadge />
      <div className="planning-validation-items">
        <div className="planning-validation-result" data-testid={`planning-validation-title-${isOnTrack ? 'on-track' : 'needs-review'}`}>
          <FormattedHTMLMessage id={`${i18nValidationKey}.Title`} values={{ portfolioName: formatLocalizedString(portfolioName), providerName }} />
        </div>
        <div className="planning-validation-item">
          {onTrackAlignment?.rateOfReturn ? <SuccessIcon /> : <WarningIcon />}
          {formatMessageWithLocale(`${i18nBase}.${onTrackAlignment?.rateOfReturn ? 'OnTrack' : 'NeedsReview'}.AnnualizeHistoricalRate`, locale, {
            providerName,
          })}
        </div>
        <div className="planning-validation-item">
          {onTrackAlignment?.assetAllocation ? <SuccessIcon /> : <WarningIcon />}
          {formatMessageWithLocale(`${i18nBase}.${onTrackAlignment?.assetAllocation ? 'OnTrack' : 'NeedsReview'}.AssetAllocation`, locale, { providerName })}
        </div>
      </div>
      <div className="planning-validation-pill">
        <PillLabel
          className={classNames('pill-label-v2', isOnTrack ? PILL_COLORS.GREEN : PILL_COLORS.YELLOW)}
          icon={`ds-${isOnTrack ? 'check' : 'warning-amber'}`}
          text={formatMessageWithLocale(`${i18nValidationKey}.Label`, locale)}
        />
      </div>
    </div>
  );

  return (
    <div className={classNames('cell planning-summary-cell', className)}>
      {!planAccount && <LoadingSpinner />}

      {planAccount && (
        <>
          <div className="planning-summary-title">
            {planAccount.name} - {formatLocalizedString(portfolioName)}
          </div>

          <div className="planning-summary-account-info">
            <div className="planning-summary-account-field">
              <span className="account-field-name">{formatMessageWithLocale('Proposal.Planning.TotalValue', locale)}</span>
              <span>
                <CurrencyCell className="account-field-value" locale={locale} decimals={decimalPlace} value={{ value: planAccount.totalValue, currency }} />
                {showBaseCurrencies && <ParenthesesLabel as="span" text={currency} />}
              </span>
            </div>
            <div className="planning-summary-account-field">
              <span className="account-field-name">{formatMessageWithLocale('Proposal.Planning.RiskProfile', locale)}</span>
              {planAccount.riskProfile ? (
                <TextCell className="account-field-value" value={formatLocalizedString(planAccount.riskProfile.title, locale)} />
              ) : (
                <NoDataCell className="account-field-value" />
              )}
            </div>
            <div className="planning-summary-account-field">
              <span className="account-field-name">{formatMessageWithLocale('Proposal.Planning.TargetRateofReturn', locale)}</span>
              <PercentageCell className="account-field-value" value={planAccount.targetRateOfReturn} decimalPlace={decimalPlace} />
            </div>
          </div>

          {renderSummaryValidation()}
        </>
      )}
    </div>
  );
};

export default PlanningSummaryCell;
